// packages/web-ui/src/panels/views/layout-with-intent.ts
//
// Composition seam: Tier-1 ELK layout (layout-builder) + Tier-2 deterministic
// post-pass (layout-postpass) under an optional `LayoutIntent`.
//
// This is deliberately its OWN module rather than living in `layout-postpass.ts`
// (which documents itself as a pure module with NO ELK import so its geometry
// math can be unit-tested in isolation) or `layout-builder.ts` (which would then
// need to import the post-pass, inverting the one-way builder→postpass edge into
// a cycle). Both the full `ViewRenderer` and the inline `MiniReactFlowPreview`
// card call this so the diagram the user sees in chat honours a chat-driven
// layout adjustment (direction / pins / groups / emphasis) exactly as the full
// renderer does — not just the content.

import type { Node, Edge } from '@xyflow/react';
import type { LayoutIntent } from '@tessa-lean/base/api-schemas/index.js';

import { runElkLayout } from './layout-builder.js';
import {
  applyLayoutPostpass,
  enlargeEmphasis,
  resolveResidualOverlaps,
  toPositionedNode,
} from './layout-postpass.js';

/**
 * Tier-1 + Tier-2 layout for a node/edge set under an optional `LayoutIntent`:
 * enlarge emphasised nodes PRE-ELK (so the graph reflows around them), run ELK
 * with the intent's ELK options, then overlay the deterministic Tier-2 post-pass
 * positions (pins / group alignment / collision-nudge) on top of ELK's result.
 *
 * Precedence: post-pass beats raw ELK; a caller may layer persisted user-drag
 * positions on top of THIS result, so a saved manual arrangement always wins.
 * When `intent` is undefined or has no pins/groups/emphasis, `enlargeEmphasis`
 * returns the nodes untouched and `applyLayoutPostpass` returns `{}`, so the
 * output is byte-identical to `runElkLayout(nodes, edges)` today (switch-OFF
 * parity). Idempotent across a renderer's up-to-3× ELK re-runs.
 */
export async function runLayoutWithIntent(
  nodes: Node[],
  edges: Edge[],
  intent: LayoutIntent | undefined,
): Promise<{ nodes: Node[]; edges: Edge[] }> {
  const enlarged = enlargeEmphasis(nodes, intent);
  const laid = await runElkLayout(enlarged, edges, intent);
  const overrides = applyLayoutPostpass(
    laid.nodes.map(toPositionedNode),
    intent,
  );
  const merged =
    Object.keys(overrides).length === 0
      ? laid.nodes
      : laid.nodes.map((n) => {
          const p = overrides[n.id];
          return p ? { ...n, position: { x: p.x, y: p.y } } : n;
        });

  // Tier-2.5 — global residual-overlap safety net. Tier-1 output is not
  // universally trustworthy (elkjs's radial once returned every node of a
  // cyclic graph at the SAME coordinate), and directive compositions can leave
  // overlaps the per-directive nudge did not own. Root-level nodes only:
  // children live in parent-relative coordinates and containers legitimately
  // overlap their children. On a clean layout this returns an empty map, so
  // the no-intent path stays byte-identical (switch-OFF parity).
  const rootNodes = merged.filter(
    (n) => (n as { parentId?: string }).parentId === undefined,
  );
  const residual = resolveResidualOverlaps(rootNodes.map(toPositionedNode));
  if (Object.keys(residual).length === 0) {
    return { nodes: merged, edges: laid.edges };
  }
  const separated = merged.map((n) => {
    const p = residual[n.id];
    return p ? { ...n, position: { x: p.x, y: p.y } } : n;
  });
  return { nodes: separated, edges: laid.edges };
}
